import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';

const BloodPressureMeasurement = ({ patientData, taskId, language = 'de', onComplete, onClose }) => {
  const [measurements, setMeasurements] = useState({
    sbp: '',
    dbp: '',
    pulse: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Charger les mesures existantes si elles existent
  useEffect(() => {
    const loadExistingMeasurements = async () => {
      if (!patientData || !patientData.patientId || !taskId) return;
      
      try {
        const response = await apiService.getQuestionnaireResponse(patientData.patientId, taskId);
        if (response.success && response.data) {
          const existingMeasurements = JSON.parse(response.data.responses);
          setMeasurements(existingMeasurements);
          setIsEditing(true);
        }
      } catch (error) {
        console.error('Erreur chargement mesures existantes:', error);
      }
    };

    loadExistingMeasurements();
  }, [patientData, taskId]);

  const handleInputChange = (field, value) => {
    // Permettre seulement les nombres positifs
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
      setMeasurements(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const isComplete = measurements.sbp !== '' && measurements.dbp !== '' && measurements.pulse !== '';
  
  // Validation des plages de valeurs
  const isValidSBP = measurements.sbp === '' || (parseInt(measurements.sbp) >= 60 && parseInt(measurements.sbp) <= 250);
  const isValidDBP = measurements.dbp === '' || (parseInt(measurements.dbp) >= 40 && parseInt(measurements.dbp) <= 160);
  const isValidPulse = measurements.pulse === '' || (parseInt(measurements.pulse) >= 25 && parseInt(measurements.pulse) <= 230);
  
  const isValid = isComplete && isValidSBP && isValidDBP && isValidPulse;

  const handleSubmit = async () => {
    if (!isValid) {
      setError(language === 'de' 
        ? 'Bitte überprüfen Sie die eingegebenen Werte. SBP: 60-250, DBP: 40-160, Puls: 25-230'
        : 'Please check the entered values. SBP: 60-250, DBP: 40-160, Pulse: 25-230'
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        // Mode édition : utiliser la route de mise à jour
        await apiService.updateQuestionnaireResponse(
          patientData.patientId,
          taskId,
          'BLOOD_PRESSURE_MEASUREMENT',
          JSON.stringify(measurements),
          null // Pas de score pour les mesures
        );
      } else {
        // Mode création : utiliser la route de soumission
        const responseData = {
          patientId: patientData.patientId,
          taskId: taskId,
          questionnaireType: 'BLOOD_PRESSURE_MEASUREMENT',
          responses: JSON.stringify(measurements),
          score: null,
          language: language,
          submissionDate: (() => {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
          })()
        };
        await apiService.submitQuestionnaire(responseData);
      }
      
      if (onComplete) {
        onComplete(null, measurements);
      }
    } catch (error) {
      console.error('Erreur soumission mesures:', error);
      setError('Erreur lors de la soumission des mesures');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="questionnaire-container">
      <div className="questionnaire-content">
        <div className="questionnaire-header">
          <h2 className="questionnaire-title">
            {language === 'de' 
              ? `Blutdruckmessung ${isEditing ? '(Bearbeiten)' : ''}`
              : `Blood Pressure Measurement ${isEditing ? '(Edit)' : ''}`
            }
          </h2>
          <button className="questionnaire-close" onClick={onClose}>×</button>
        </div>

        <div className="questionnaire-instructions">
          <div className="blood-pressure-tutorial">
            <div className="tutorial-image-container">
              <img 
                src="/images/blood_pressure.jpg" 
                alt={language === 'de' ? 'Blutdruckmessung Anleitung' : 'Blood Pressure Measurement Guide'}
                className="tutorial-image"
              />
            </div>
            <div className="tutorial-instructions">
              <h4>{language === 'de' ? 'Anleitung zur Blutdruckmessung:' : 'Blood Pressure Measurement Instructions:'}</h4>
              <ul>
                <li>{language === 'de' ? 'Setzen Sie sich ruhig hin und entspannen Sie sich 5 Minuten' : 'Sit quietly and relax for 5 minutes'}</li>
                <li>{language === 'de' ? 'Legen Sie den Arm auf eine stabile Oberfläche' : 'Place your arm on a stable surface'}</li>
                <li>{language === 'de' ? 'Wickeln Sie die Manschette um den Oberarm' : 'Wrap the cuff around your upper arm'}</li>
                <li>{language === 'de' ? 'Messen Sie in Ruhe und sprechen Sie nicht während der Messung' : 'Measure at rest and do not speak during measurement'}</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="measurements-form">
          <div className="measurement-field">
            <label className="input-label">
              {language === 'de' ? 'Systolischer Blutdruck:' : 'Systolic Blood Pressure:'}
              <input
                type="number"
                min="60"
                max="250"
                value={measurements.sbp}
                onChange={(e) => handleInputChange('sbp', e.target.value)}
                className="measurement-input"
                placeholder={language === 'de' ? 'mmHg' : 'mmHg'}
              />
            </label>
          </div>

          <div className="measurement-field">
            <label className="input-label">
              {language === 'de' ? 'Diastolischer Blutdruck:' : 'Diastolic Blood Pressure:'}
              <input
                type="number"
                min="40"
                max="160"
                value={measurements.dbp}
                onChange={(e) => handleInputChange('dbp', e.target.value)}
                className="measurement-input"
                placeholder={language === 'de' ? 'mmHg' : 'mmHg'}
              />
            </label>
          </div>

          <div className="measurement-field">
            <label className="input-label">
              {language === 'de' ? 'Puls:' : 'Pulse:'}
              <input
                type="number"
                min="25"
                max="230"
                value={measurements.pulse}
                onChange={(e) => handleInputChange('pulse', e.target.value)}
                className="measurement-input"
                placeholder={language === 'de' ? 'Schläge/min' : 'beats/min'}
              />
            </label>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="questionnaire-footer">
          <button
            className={`submit-btn ${!isValid || loading ? 'disabled' : ''}`}
            onClick={handleSubmit}
            disabled={!isValid || loading}
          >
            {loading 
              ? (language === 'de' ? 'Wird gesendet...' : 'Submitting...')
              : (language === 'de' ? 'Absenden' : 'Submit')
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default BloodPressureMeasurement; 